import {
  ArrowBack,
  Notifications as Bell,
  Chat,
  Delete,
  EmojiEvents,
  MarkChatRead,
  LocalOffer as Tag,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography,
  alpha,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaHandshake, FaUsers } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import api from '../config/axiosConfig';
import { useTheme } from '../contexts/ThemeContext';

const NotificationsPage = () => {
  const { t } = useTranslation();
  const { darkMode, theme } = useTheme();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications/all');
      const enriched = res.data.map(n => ({
        ...n,
        id: n._id || n.id || `notif-${Date.now()}-${Math.random()}`,
        read: n.read ?? n.isRead ?? false,
        title: n.title || 'Notification',
        message: n.message || n.description,
      }));
      setNotifications(enriched);
      setFiltered(enriched);
    } catch (err) {
      setError(t('notification.error.load'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const types = ['all', 'certificate', 'message', 'pysa', 'mention'];
    const type = types[tab];
    setFiltered(
      type === 'all'
        ? notifications
        : notifications.filter(n => n.source === type || n.type === type)
    );
  }, [tab, notifications]);

  const markAsRead = async (id) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
    window.dispatchEvent(new CustomEvent('notification-read'));
  };

  const deleteNotif = async (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleClick = (notif) => {
    markAsRead(notif.id);
    if (notif.action === 'open-chat') navigate(`/pysa/chat/${notif.conversationId}`);
    if (notif.action === 'view-post') navigate(`/pysa/post/${notif.postId}`);
    if (notif.action === 'view-certificate') navigate(`/certificates`);
  };

  const getIcon = (type) => {
    const icons = {
      certificate: <EmojiEvents sx={{ color: '#ffd700' }} />,
      message: <Chat sx={{ color: '#2196f3' }} />,
      mention: <Tag sx={{ color: '#ff9800' }} />,
      pysa: <FaHandshake sx={{ color: '#9c27b0' }} />,
    };
    return icons[type] || <Bell />;
  };

  const getBg = (type, source, read) => {
    if (read) return alpha(theme.palette.grey[darkMode ? 800 : 300], 0.3);
    const colors = {
      certificate: '#f9fbe7',
      message: '#e3f2fd',
      mention: '#fff3e0',
      pysa: '#f3e5f5',
    };
    return colors[type] || '#f5f5f5';
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: darkMode ? '#0F172A' : '#F4F7FE' }}>
      <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.vibrantColors.tasks }} />
    </Box>
  );

  if (error) return (
    <Container sx={{ mt: 16, bgcolor: darkMode ? '#0F172A' : '#F4F7FE', minHeight: '100vh' }}>
      <Alert severity="error" sx={{ borderRadius: 3, boxShadow: 3 }}>
        {error}
      </Alert>
    </Container>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: darkMode ? '#0F172A' : '#F4F7FE',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* === NAVBAR PYSA === */}
      <Box
        sx={{
          bgcolor: darkMode ? '#1E293B' : '#FFFFFF',
          boxShadow: `0 4px 12px ${darkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          borderBottom: `1px solid ${darkMode ? '#334155' : '#E5E7EB'}`,
        }}
      >
        <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/pysa')} sx={{ color: theme.palette.vibrantColors.tasks }}>
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
              }}
            >
              Notifications
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton onClick={() => navigate('/pysa')}>
              <FaUsers size={22} color={theme.palette.vibrantColors.tasks} />
            </IconButton>
            <IconButton onClick={() => navigate('/dashboard')}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.vibrantColors.progression }}>
                Dashboard
              </Typography>
            </IconButton>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1, py: 4 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
              textAlign: 'center',
              mb: 3,
            }}
          >
            {t('notification.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
            Toutes vos activités en un seul endroit.
          </Typography>
        </motion.div>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {[
            { label: 'Toutes', icon: <Bell />, count: notifications.length },
            { label: 'Certificats', icon: <EmojiEvents />, count: notifications.filter(n => n.source === 'certificate').length },
            { label: 'Messages', icon: <Chat />, count: notifications.filter(n => n.source === 'message').length },
            { label: 'Pysa', icon: <FaHandshake />, count: notifications.filter(n => n.source === 'pysa').length },
            { label: 'Mentions', icon: <Tag />, count: notifications.filter(n => n.source === 'mention').length },
          ].map((item, i) => (
            <Tab
              key={i}
              label={
                <Badge badgeContent={item.count} color="error">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {item.icon}
                    {item.label}
                  </Box>
                </Badge>
              }
            />
          ))}
        </Tabs>

        <AnimatePresence mode="wait">
          {filtered.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  textAlign: 'center',
                  bgcolor: alpha(theme.palette.grey[darkMode ? 900 : 100], 0.5),
                  borderRadius: 4,
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <Bell sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Aucune notification pour le moment
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Revenez plus tard ou explorez Pysa !
                </Typography>
              </Paper>
            </motion.div>
          ) : (
            <List disablePadding>
              {filtered.map((notif, index) => (
                <motion.div
                  key={notif.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <ListItemButton
                    onClick={() => handleClick(notif)}
                    sx={{
                      bgcolor: getBg(notif.source || notif.type, notif.read),
                      borderRadius: 3,
                      mb: 1.5,
                      p: 2,
                      border: notif.read ? 'none' : `2px solid ${theme.palette.primary.main}`,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: alpha(getBg(notif.source || notif.type, notif.read), 0.7),
                        transform: 'translateY(-2px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      >
                        {getIcon(notif.source || notif.type)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={notif.read ? 500 : 700}>
                            {notif.title}
                          </Typography>
                          {notif.category && (
                            <Chip label={notif.category} size="small" color="primary" />
                          )}
                          {!notif.read && (
                            <Chip label="Nouveau" size="small" color="error" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                            {notif.message}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notif.timestamp).toLocaleString('fr-FR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </Typography>
                        </Box>
                      }
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!notif.read && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notif.id);
                          }}
                        >
                          <MarkChatRead fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotif(notif.id);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemButton>
                </motion.div>
              ))}
            </List>
          )}
        </AnimatePresence>
      </Container>
    </Box>
  );
};

export default NotificationsPage;